/* screens/gift/GiftMy.jsx — «Мои подарки» (/gift/my).

   Кабинет дарителя. Он отвечает на единственный вопрос, ради которого сюда
   заходят: «дошло ли». Поэтому статус доставки стоит в карточке рядом с кодом,
   а не прячется в детали, и показывается ЧЕСТНО: «не удалось отправить» — это
   рабочее состояние, о котором даритель обязан узнать от нас, а не от
   получателя, который ничего не получил.

   Правка (почта, дата, подпись) доступна, пока письмо не ушло и код не
   активирован — сервер решает это сам (`is_editable`), фронт лишь показывает.
   Дублировать правило здесь нельзя: разъехавшись, интерфейс предложил бы
   кнопку, на которую сервер ответит отказом. */

import React from 'react';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { downloadFile } from '../../lib/api/client.js';
import * as giftsApi from '../../lib/api/gifts.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';

const { useState, useEffect, useCallback } = React;
const { useNavigate } = RR;

const money = (v) => (v == null || v === '' ? '—' : fmt.money(Number(v)));

const CODE_STATE = {
  active: { label: 'Не активирован', tone: 'info' },
  redeemed: { label: 'Активирован', tone: 'ok' },
  expired: { label: 'Срок истёк', tone: 'warn' },
  revoked: { label: 'Аннулирован', tone: 'bad' },
};

const DELIVERY_STATE = {
  none: { label: 'Без отправки', tone: 'mute' },
  scheduled: { label: 'Отправим по расписанию', tone: 'info' },
  sent: { label: 'Письмо отправлено', tone: 'ok' },
  failed: { label: 'Письмо не ушло', tone: 'bad' },
};

function Tag({ tone, children }) {
  return <span className={'gift-tag gift-tag--' + tone}>{children}</span>;
}

function humanDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
    .replace(/\s*г\.$/, '');
}

function humanSchedule(local) {
  // Строка приходит уже в МСК без пояса ('2026-09-01T14:00') — показываем как есть,
  // подписав пояс. Прогонять её через Date() нельзя: браузер прочитал бы её в
  // СВОЁМ поясе и показал бы клиенту из Владивостока другое время.
  if (!local) return '';
  const [date, time] = local.split('T');
  const [y, m, d] = date.split('-');
  return `${d}.${m}.${y}, ${time} МСК`;
}

function EditModal({ gift, onClose, onSaved }) {
  const toast = useToast();
  const [email, setEmail] = useState(gift ? gift.recipient_email : '');
  const [name, setName] = useState(gift ? gift.recipient_name : '');
  const [when, setWhen] = useState(gift ? gift.scheduled_for : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!gift) return;
    setEmail(gift.recipient_email);
    setName(gift.recipient_name);
    setWhen(gift.scheduled_for);
    setError(null);
  }, [gift]);

  if (!gift) return null;

  const save = async () => {
    setBusy(true);
    setError(null);
    const r = await giftsApi.updateGift(gift.id, {
      recipient_email: email.trim(),
      recipient_name: name.trim(),
      sender_name: gift.sender_name,
      message: gift.message,
      scheduled_for: when,
    });
    setBusy(false);
    if (!r.ok) {
      setError((r.data && r.data.detail) || 'Не удалось сохранить.');
      return;
    }
    toast.show('Сохранено', 'success');
    onSaved(r.data);
  };

  return (
    <Modal open onClose={onClose} title="Кому и когда" size="sm">
      <div className="stack stack-12">
        {error && (
          <div className="callout callout--bad" role="alert">
            <Icon name="alert-circle" size={16} /><span>{error}</span>
          </div>
        )}
        <Field label="Имя получателя" htmlFor="gm-name">
          <TextInput id="gm-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Почта получателя" htmlFor="gm-email"
          hint="Пусто — письмо не отправляем, сертификат вручаете сами">
          <TextInput id="gm-email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Когда отправить (МСК)" htmlFor="gm-when"
          hint="Пусто — не отправлять по расписанию">
          <TextInput id="gm-when" type="datetime-local" value={when}
            onChange={(e) => setWhen(e.target.value)} />
        </Field>
        <div className="cluster cluster-8">
          <Button variant="primary" loading={busy} onClick={save}>Сохранить</Button>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
        </div>
      </div>
    </Modal>
  );
}

function GiftCard({ gift, onEdit, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const state = CODE_STATE[gift.status] || CODE_STATE.active;
  const delivery = DELIVERY_STATE[gift.delivery_status] || DELIVERY_STATE.none;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(gift.code);
      toast.show('Код скопирован', 'success');
    } catch {
      toast.show('Не удалось скопировать — выделите код вручную', 'muted');
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      // downloadFile — канон дома: сам тянет куки и X-Workspace-Id. Голый
      // <a href> уронил бы скачивание из бизнес-пространства.
      await downloadFile(giftsApi.giftPdfUrl(gift.id), { filename: `gift-${gift.code}.pdf` });
    } catch {
      toast.show('Не удалось скачать сертификат', 'danger');
    }
    setBusy(false);
  };

  const sendNow = async () => {
    setBusy(true);
    const r = await giftsApi.sendGiftNow(gift.id);
    setBusy(false);
    if (!r.ok) {
      toast.show((r.data && r.data.detail) || 'Не удалось отправить', 'danger');
      return;
    }
    const ok = r.data && r.data.delivery_status === 'sent';
    toast.show(ok ? 'Письмо отправлено' : 'Письмо не ушло — попробуйте позже',
      ok ? 'success' : 'danger');
    onChanged(r.data);
  };

  return (
    <article className="gift-item">
      <header className="gift-item__head">
        <div className="gift-item__title">
          <span className="t-h4">{gift.plan.name}</span>
          <span className="t-caption text-mute">
            {gift.plan.audience === 'business' ? 'для заведения' : 'личный'} ·{' '}
            {money(gift.amount_rub)}
          </span>
        </div>
        <Tag tone={state.tone}>{state.label}</Tag>
      </header>

      <button type="button" className="gift-item__code" onClick={copy}
        title="Скопировать код">
        <span className="num">{gift.code}</span>
        <Icon name="copy" size={15} />
      </button>

      <dl className="gift-item__meta">
        <div><dt>Кому</dt><dd>{gift.recipient_name || gift.recipient_email || 'не указан'}</dd></div>
        <div><dt>Действует до</dt><dd>{humanDate(gift.valid_until)}</dd></div>
        {gift.scheduled_for && !gift.sent_at && (
          <div><dt>Отправим</dt><dd>{humanSchedule(gift.scheduled_for)}</dd></div>
        )}
        {gift.redeemed_at && (
          <div><dt>Активирован</dt><dd>{humanDate(gift.redeemed_at)}
            {gift.redeemed_workspace ? ` · ${gift.redeemed_workspace}` : ''}</dd></div>
        )}
      </dl>

      <footer className="gift-item__foot">
        <Tag tone={delivery.tone}>{delivery.label}</Tag>
        <div className="cluster cluster-8">
          <Button size="sm" variant="ghost" iconLeft="download" loading={busy}
            onClick={download}>PDF</Button>
          {gift.is_editable && (
            <Button size="sm" variant="ghost" iconLeft="edit"
              onClick={() => onEdit(gift)}>Изменить</Button>
          )}
          {gift.is_editable && gift.recipient_email && (
            <Button size="sm" variant="secondary" iconLeft="send" loading={busy}
              onClick={sendNow}>Отправить сейчас</Button>
          )}
        </div>
      </footer>
    </article>
  );
}

function GiftMy() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading');
  const [gifts, setGifts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await giftsApi.listMyGifts();
    if (!r.ok) { setPhase('error'); return; }
    setGifts((r.data && r.data.gifts) || []);
    setPhase('ready');
  }, []);

  useEffect(() => { load(); }, [load]);

  const replace = (updated) => {
    setGifts((rows) => rows.map((g) => (g.id === updated.id ? { ...g, ...updated } : g)));
    setEditing(null);
  };

  return (
    <div className="section-page gift-section">
      <PageHeader title="Мои подарки" subtitle="Подарок" />
      <div className="section-body gift-page">
        <p className="t-body text-dim gift-lead">
          Купленные сертификаты: код, доставка и статус активации.
        </p>

      <QueryBoundary
        loading={phase === 'loading'}
        error={phase === 'error' ? new Error('failed') : null}
        onRetry={load}
        skeleton={<div className="stack stack-12">
          <span className="skeleton" style={{ height: 150, borderRadius: 'var(--radius-lg)' }} />
          <span className="skeleton" style={{ height: 150, borderRadius: 'var(--radius-lg)' }} />
        </div>}>

        {gifts.length === 0 ? (
          <div className="gift-empty">
            <Icon name="gift" size={28} className="text-mute" />
            <h2 className="t-h4">Подарков пока нет</h2>
            <p className="t-body text-dim">
              Оплатите тариф для друга или партнёра — мы выпустим сертификат
              с кодом, а вы решите, отправить его письмом или вручить лично.
            </p>
            <Button variant="primary" size="lg" onClick={() => navigate('/gift')}>
              Подарить Ресториум
            </Button>
          </div>
        ) : (
          <>
            <div className="gift-list">
              {gifts.map((g) => (
                <GiftCard key={g.id} gift={g} onEdit={setEditing} onChanged={replace} />
              ))}
            </div>
            <div className="gift-actions">
              <Button variant="secondary" size="lg" iconLeft="plus"
                onClick={() => navigate('/gift')}>Подарить ещё</Button>
            </div>
          </>
        )}
      </QueryBoundary>

      </div>

      <EditModal gift={editing} onClose={() => setEditing(null)} onSaved={replace} />
    </div>
  );
}

export { GiftMy };
